// Shared Dispatch constants — single source of truth for the frontend, mirroring
// src/shared/dispatch/stages.js on the backend (client-approved audit, Dispatch
// rework). Values must match the Mongoose enums exactly; labels are display-only.

/**
 * The client's dispatch pipeline, in the exact order they operate it.
 *
 * Lives ALONGSIDE — not instead of — the challan `status` lifecycle
 * (draft / dispatched / delivered / returned / cancelled) that the challan list
 * already renders. `status` is untouched by anything on this screen; this is a
 * second, independent field on the same document (`dispatchStage`, see the
 * schema comment in src/db-accounts.js), the same way `pipeline_stage` sits
 * alongside `status` on a Lead.
 */
export const DISPATCH_STAGES = [
  'order_verification',
  'credit_payment_check',
  'stock_production_check',
  'dispatch_planning',
  'material_allocation_qc',
  'packing_label_verification',
  'vehicle_transport_confirmation',
  'dispatch_approval',
  'loading',
  'documents_generated',
  'material_dispatched',
  'lr_tracking_updated',
  'customer_delivery_confirmation',
  'pod_collection',
  'invoice_closure_payment_followup',
] as const;

export type DispatchStage = (typeof DISPATCH_STAGES)[number];

export const DISPATCH_STAGE_LABELS: Record<DispatchStage, string> = {
  order_verification: 'Order Verification',
  credit_payment_check: 'Credit & Payment Check',
  stock_production_check: 'Stock/Production Check',
  dispatch_planning: 'Dispatch Planning',
  material_allocation_qc: 'Material Allocation & QC',
  packing_label_verification: 'Packing/Label Verification',
  vehicle_transport_confirmation: 'Vehicle & Transport Confirmation',
  dispatch_approval: 'Dispatch Approval',
  loading: 'Loading',
  documents_generated: 'Documents Generated',
  material_dispatched: 'Material Dispatched',
  lr_tracking_updated: 'LR/Tracking Updated',
  customer_delivery_confirmation: 'Customer Delivery Confirmation',
  pod_collection: 'POD Collection',
  invoice_closure_payment_followup: 'Invoice Closure & Payment Follow-up',
};

export const DEFAULT_DISPATCH_STAGE: DispatchStage = 'order_verification';

/** Packaging vocabulary. '' is a real member — "not specified" on old documents. */
export const PACKAGING_TYPES = [
  '', 'box', 'bucket', 'can', 'barrel', 'drum', 'jerrycan', 'ibc_tote', 'tanker',
  'bulk', 'carton', 'bottle', 'pouch', 'bag', 'other',
] as const;

export type PackagingType = (typeof PACKAGING_TYPES)[number];

export const PACKAGING_TYPE_LABELS: Record<PackagingType, string> = {
  '': 'Not specified',
  box: 'Box',
  bucket: 'Bucket',
  can: 'Can',
  barrel: 'Barrel',
  drum: 'Drum',
  jerrycan: 'Jerrycan',
  ibc_tote: 'IBC / Tote',
  tanker: 'Tanker',
  bulk: 'Bulk',
  carton: 'Carton',
  bottle: 'Bottle',
  pouch: 'Pouch',
  bag: 'Bag',
  other: 'Other',
};

/** One finding from the credit/stock gate, as returned by the backend. */
export interface DispatchCheckFinding {
  code: string;
  message: string;
  productId?: string;
  available?: number;
  requested?: number;
}

export interface AdvancePaymentCheck {
  ok: boolean;
  requiredAdvancePct: number;
  requiredAdvance: number;
  received: number;
  shortfall: number;
  orderNumber: string;
  /** The live invoice a new payment should be recorded against, or null when
   *  no invoice has been raised against this order yet. */
  invoiceId: string | null;
}

export interface DispatchCheckResult {
  allowed: boolean;
  blockers: DispatchCheckFinding[];
  warnings: DispatchCheckFinding[];
  creditCheckSkipped: boolean;
  partyContactId: string | null;
  /** null when there's no linked sales order or the customer needs 0% advance. */
  advancePayment: AdvancePaymentCheck | null;
}

export interface PodFileRef {
  storageKey: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string | null;
}

export interface ProofOfDelivery {
  captured: boolean;
  receiverName: string;
  deliveredBy: string;
  customerConfirmedAt: string | null;
  signatureFile?: PodFileRef;
  photoFiles?: PodFileRef[];
  notes: string;
  capturedAt: string | null;
  documentNumber?: string;
}

/** Base path for every dispatch route. Kept here so no component hard-codes it. */
export const DISPATCH_API_BASE = '/api/accounts/commercial-documents/dispatch';
